<template>
  <div class="ms-3 mt-3">
    <h3 class="mb-3 fw-bold spacing-close">AI Insights</h3>
    
    <insights-tabs
      :view-model="viewModel"
      :sessions-version="sessionsVersion"
      @tabChangedEvent="onChildViewSelected"
    ></insights-tabs>

    <div>
      <prompts-manager
        v-if="viewModel.childView === 'prompts'"
        :view-model="viewModel"
      ></prompts-manager>

      <div v-if="viewModel.childView === 'conversations'" class="mt-2">
        <conversations-view
          v-if="sessionsVersion === '1.0'"
          :view-model="viewModel"
          :sessions-version="sessionsVersion"
          :filters="filters"
          :search-term="searchTerm"
          :date-filter="dateFilter"
          :start-date="startDate"
          :end-date="endDate"
          :dates="dates"
          :sorting="sorting"
          @conversationSelected="onConversationSelected"
          @filtersChanged="onFiltersChanged"
          @searchTermChanged="onSearchTermChanged"
          @dateFilterChanged="onDateFilterChanged"
          @customDatesChanged="onCustomDatesChanged"
          @sortingChanged="onSortingChanged"
          @sessionsVersionChanged="onSessionsVersionChanged"
        ></conversations-view>
        <sessions-v2-view
          v-else
          :view-model="viewModel"
          :sessions-version="sessionsVersion"
          :filters="filters"
          :search-term="searchTerm"
          :date-filter="dateFilter"
          :start-date="startDate"
          :end-date="endDate"
          :dates="dates"
          :sorting="sorting"
          @sessionsVersionChanged="onSessionsVersionChanged"
          @sessionSelected="onSessionSelected"
          @filtersChanged="onFiltersChanged"
          @searchTermChanged="onSearchTermChanged"
          @dateFilterChanged="onDateFilterChanged"
          @customDatesChanged="onCustomDatesChanged"
          @sortingChanged="onSortingChanged"
        ></sessions-v2-view>
      </div>

      <conversation-details
        v-if="viewModel.childView === 'conversation-details' && sessionsVersion === '1.0'"
        :view-model="viewModel"
        :filters="filters"
        :search-term="searchTerm"
        :date-filter="dateFilter"
        :start-date="startDate"
        :end-date="endDate"
        :dates="dates"
        :sorting="sorting"
        @backToList="clearConversationState"
      ></conversation-details>
      <session-details-v2
        v-if="viewModel.childView === 'conversation-details' && sessionsVersion === '2.0'"
        :session="viewModel.selectedSession"
        :details="viewModel.sessionDetails"
        :loading="sessionDetailsLoading"
        :error="sessionDetailsError"
        @backToList="clearConversationState"
        @retry="retrySelectedSession"
      ></session-details-v2>
      <analytics-dashboard
        v-if="viewModel.childView === 'analytics-dashboard'"
        :view-model="viewModel"
      ></analytics-dashboard>
    </div>
  </div>
</template>

<script>
import InsightsTabs from "../components/InsightsTabs";
import PromptsManager from "./insights/PromptsManager";
import ConversationsView from "./insights/ConversationsView";
import SessionsV2View from "./insights/SessionsV2View";
import ConversationDetails from "./insights/ConversationDetails";
import SessionDetailsV2 from "./insights/SessionDetailsV2";
import AnalyticsDashboard from "./insights/AnalyticsDashboard";
import AppUtil from "../utils/AppUtil";
import InsightsService from "../services/InsightsService";

export default {
  name: "AiInsights",
  components: {
    InsightsTabs,
    PromptsManager,
    ConversationsView,
    SessionsV2View,
    ConversationDetails,
    SessionDetailsV2,
    AnalyticsDashboard,
  },
  data() {
    return {
      model: streetmetrics.model,
      viewModel: {
        childView: "prompts", 
        disabledTabs: ["conversation-details"],
        selectedConversation: null,
        conversationDetails: null,
        selectedSession: null,
        sessionDetails: null,
      },
      sessionsVersion: '1.0', // '1.0' | '2.0'
      // Conversations / sessions list filter state (lifted so overview↔detail survives v-if)
      searchTerm: '',
      filters: {
        customerId: '',
        userId: '',
        initiationType: '',
        model: '',
      },
      sorting: {
        field: null, // 'id' or 'created'
        direction: null, // 'asc' or 'desc'
      },
      dateFilter: 'all',
      startDate: null,
      endDate: null,
      dates: [],
      appUtil: new AppUtil(),
      insightsService: new InsightsService(),
      sessionDetailsLoading: false,
      sessionDetailsError: null,
    };
  },
  methods: {
    onChildViewSelected(childView) {
      if (this.viewModel.childView === 'conversation-details' && childView !== 'conversation-details') {
        this.clearConversationState();
      }
      
      this.viewModel.childView = childView;
    },
    onSessionsVersionChanged(version) {
      if (this.sessionsVersion === version) {
        return;
      }
      this.sessionsVersion = version;
      this.clearConversationState({ stayOnList: true });
    },
    onConversationSelected({ conversation, details }) {
      // Enable the details tab and set the selected conversation
      this.viewModel.disabledTabs = this.viewModel.disabledTabs.filter(tab => tab !== 'conversation-details');
      this.viewModel.selectedConversation = conversation;
      this.viewModel.conversationDetails = details;
      this.viewModel.selectedSession = null;
      this.viewModel.sessionDetails = null;
      this.sessionDetailsError = null;
      
      // Switch to the details tab
      this.viewModel.childView = 'conversation-details';
    },
    onSessionSelected({ session, details }) {
      this.viewModel.disabledTabs = this.viewModel.disabledTabs.filter(tab => tab !== 'conversation-details');
      this.viewModel.selectedSession = session;
      this.viewModel.sessionDetails = details;
      this.viewModel.selectedConversation = null;
      this.viewModel.conversationDetails = null;
      this.sessionDetailsError = null;
      this.viewModel.childView = 'conversation-details';
    },
    async retrySelectedSession() {
      if (!this.viewModel.selectedSession?.id) {
        return;
      }
      this.sessionDetailsLoading = true;
      this.sessionDetailsError = null;
      try {
        this.viewModel.sessionDetails = await this.insightsService.getAdminSessionDetails(
          this.viewModel.selectedSession.id,
        );
      } catch (error) {
        this.sessionDetailsError = error.message || 'Failed to load session details';
      } finally {
        this.sessionDetailsLoading = false;
      }
    },
    clearConversationState({ stayOnList = false } = {}) {
      this.viewModel.selectedConversation = null;
      this.viewModel.conversationDetails = null;
      this.viewModel.selectedSession = null;
      this.viewModel.sessionDetails = null;
      this.sessionDetailsError = null;
      if (!this.viewModel.disabledTabs.includes('conversation-details')) {
        this.viewModel.disabledTabs = [...this.viewModel.disabledTabs, 'conversation-details'];
      }
      
      if (!stayOnList) {
        this.viewModel.childView = 'conversations';
      }
    },
    // Conversations / sessions list filter handlers
    onFiltersChanged(newFilters) {
      this.filters = { ...newFilters };
    },
    onSearchTermChanged(newSearchTerm) {
      this.searchTerm = newSearchTerm;
    },
    onDateFilterChanged(newDateFilter) {
      this.dateFilter = newDateFilter;

      if (newDateFilter === 'all') {
        this.startDate = null;
        this.endDate = null;
        this.dates = [];
      } else if (newDateFilter !== 'custom') {
        const startAndEndDates = this.appUtil.getFilterDates(newDateFilter);
        this.startDate = startAndEndDates.start;
        this.endDate = startAndEndDates.end;
        this.dates = [startAndEndDates.start, startAndEndDates.end];
      }
    },
    onCustomDatesChanged(newDates) {
      if (newDates && newDates.length === 2) {
        this.startDate = newDates[0];
        this.endDate = newDates[1];
        this.dates = [...newDates];
      }
    },
    onSortingChanged(newSorting) {
      this.sorting = { ...newSorting };
    },
  },
  mounted() {
    this.onDateFilterChanged('all');
  },
};
</script>

<style></style> 